import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Shield, Lock, ChevronDown } from "lucide-react";
import { PrivacyPolicyContent } from "./PrivacyPolicyContent";

interface PrivacyConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccept: (marketingConsent: boolean) => void;
  isSubmitting?: boolean;
}

export function PrivacyConsentModal({
  open,
  onOpenChange,
  onAccept,
  isSubmitting = false,
}: PrivacyConsentModalProps) {
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [marketingAgreed, setMarketingAgreed] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setPrivacyAgreed(false);
      setMarketingAgreed(false);
      setHasScrolledToBottom(false);
    }
  }, [open]);

  const handleScroll = () => {
    const element = scrollRef.current;
    if (!element) return;
    
    const scrollPosition = element.scrollTop + element.clientHeight;
    const scrollHeight = element.scrollHeight;
    const threshold = 50;
    
    if (scrollHeight - scrollPosition <= threshold) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = () => {
    if (privacyAgreed) {
      onAccept(marketingAgreed);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setPrivacyAgreed(false);
      setMarketingAgreed(false);
      setHasScrolledToBottom(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl h-[90dvh] sm:h-[85vh] overflow-hidden p-4 sm:p-6 flex flex-col">
        {/* Header - compact */}
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            <DialogTitle className="font-display text-lg sm:text-xl">
              Privacy & Data Collection Notice
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Scrollable content area - takes most of the space */}
        <div 
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto pr-2 -mr-2 overscroll-contain relative"
          style={{ 
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
          }}
          onScroll={handleScroll}
        >
          {/* Scroll indicator - sticky at top of scroll area */}
          {!hasScrolledToBottom && (
            <div className="sticky top-0 z-10 flex items-center justify-center gap-2 py-2 text-xs sm:text-sm text-muted-foreground bg-muted/90 backdrop-blur-sm rounded-md mb-3">
              <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 animate-bounce" />
              <span>Scroll to read full Privacy Policy</span>
              <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 animate-bounce" />
            </div>
          )}
          
          <div className="prose prose-sm dark:prose-invert max-w-none pb-4">
            <p className="text-xs sm:text-sm text-muted-foreground mb-4">
              By creating an account, you acknowledge that Wellness Connect 
              will collect, use, and disclose your personal information in accordance 
              with the Privacy Policy below and the applicable privacy laws.
            </p>
            <PrivacyPolicyContent />
          </div>
        </div>

        {/* Consent checkboxes - compact on mobile */}
        <div className="flex-shrink-0 space-y-2 sm:space-y-3 py-2 sm:py-3 border-t border-border overflow-y-auto max-h-[15vh] sm:max-h-[20vh]">
          <div 
            className={`flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-lg border border-border bg-background touch-manipulation cursor-pointer ${!hasScrolledToBottom ? 'opacity-50' : ''}`}
            onClick={() => hasScrolledToBottom && setPrivacyAgreed(!privacyAgreed)}
          >
            <Checkbox
              id="privacy-consent"
              checked={privacyAgreed}
              onCheckedChange={(checked) => hasScrolledToBottom && setPrivacyAgreed(checked === true)}
              disabled={!hasScrolledToBottom}
              className="h-4 w-4 sm:h-5 sm:w-5 mt-0.5 flex-shrink-0"
            />
            <Label
              htmlFor="privacy-consent"
              className="text-xs sm:text-sm leading-tight sm:leading-relaxed cursor-pointer flex-1"
            >
              <span className="font-medium text-primary">Required:</span> I agree to the Privacy Policy
            </Label>
          </div>

          <div 
            className="flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-lg border border-border bg-background touch-manipulation cursor-pointer"
            onClick={() => setMarketingAgreed(!marketingAgreed)}
          >
            <Checkbox
              id="marketing-consent"
              checked={marketingAgreed}
              onCheckedChange={(checked) => setMarketingAgreed(checked === true)}
              className="h-4 w-4 sm:h-5 sm:w-5 mt-0.5 flex-shrink-0"
            />
            <Label
              htmlFor="marketing-consent"
              className="text-xs sm:text-sm leading-tight sm:leading-relaxed cursor-pointer text-muted-foreground flex-1"
            >
              <span className="font-medium text-foreground">Optional:</span> Receive updates & promotions
            </Label>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-muted-foreground px-1">
            <Lock className="w-3 h-3 flex-shrink-0" />
            <span>Protected under Privacy Act 1988 (Cth)</span>
          </div>
        </div>

        {/* Footer - compact buttons on mobile */}
        <DialogFooter className="flex-shrink-0 pt-3 pb-4 sm:pb-0 gap-2 sm:gap-0 flex-col-reverse sm:flex-row overflow-visible">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
            className="touch-manipulation w-full sm:w-auto min-h-[44px]"
          >
            Cancel
          </Button>
          <Button
            variant="hero"
            onClick={handleAccept}
            disabled={!privacyAgreed || isSubmitting}
            className="touch-manipulation w-full sm:w-auto min-h-[44px]"
          >
            {isSubmitting ? "Processing..." : "I Agree & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
